import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS } from "@/constants/content";
import { HOME } from "@/constants/testIds";

const smoothScroll = (href, close) => (e) => {
  if (href.startsWith("#")) {
    e.preventDefault();
    const el = document.querySelector(href === "#top" ? "body" : href);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    close?.();
  }
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled ? "py-2" : "py-3 sm:py-4"
      }`}
    >
      <div className="container-x">
        <div
          className={`flex items-center justify-between gap-4 rounded-2xl px-3 sm:px-4 py-2 sm:py-2.5 transition-all ${
            scrolled
              ? "glass-light shadow-premium"
              : "bg-white/40 backdrop-blur-md border border-white/60"
          }`}
        >
          <a href="#top" onClick={smoothScroll("#top")} aria-label="BoostF home">
            <Logo />
          </a>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={smoothScroll(l.href)}
                className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 rounded-full hover:bg-slate-900/5 transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex">
              <a
                href="#reserve"
                onClick={smoothScroll("#reserve")}
                data-testid={HOME.navReserve}
                className="btn-primary text-sm"
              >
                Reserve My Seat
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </span>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid={HOME.navMobileToggle}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 bg-white/70"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="md:hidden mt-2 glass-light rounded-2xl shadow-premium overflow-hidden"
            >
              <div className="p-2">
                {NAV_LINKS.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={smoothScroll(l.href, () => setOpen(false))}
                    className="block px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-900/5 rounded-xl"
                  >
                    {l.label}
                  </a>
                ))}
                <a
                  href="#reserve"
                  onClick={smoothScroll("#reserve", () => setOpen(false))}
                  className="btn-primary w-full justify-center mt-2"
                >
                  Reserve My Seat
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Navbar;
